import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Switch, Alert, Space, Divider, Radio, Spin, Checkbox } from 'antd';
import { useTheme } from '@/components/common/ThemeProvider';
import {
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    LockOutlined
} from '@ant-design/icons';
import {NubUserDTO, NubGroupDTO, NubAccessLevelDTO} from '../types';
import { PasswordConfig } from './PasswordConfigDialog';
import { useSearchCustomers1 } from '@/shared/api/client/customers';
import { useGetAllBranches } from '@/shared/api/client/company-resource';

const { Option } = Select;

interface NubUserFormDialogProps {
    open: boolean;
    mode: 'view' | 'add' | 'edit' | 'delete';
    initialData?: NubUserDTO | null;
    groups: NubGroupDTO[];
    accessLevels: NubAccessLevelDTO[];
    passwordConfig: PasswordConfig;
    onCancel: () => void;
    onSubmit: (data: NubUserDTO) => Promise<void>;
}

const UserType = {
    MEMBER: 'MEMBER',
    STAFF: 'STAFF',
    SYSTEM: 'SYSTEM'
} as const;

export const NubUserFormDialog: React.FC<NubUserFormDialogProps> = ({
                                                                        open,
                                                                        mode,
                                                                        initialData,
                                                                        groups,
                                                                        accessLevels,
                                                                        passwordConfig,
                                                                        onCancel,
                                                                        onSubmit,
                                                                    }) => {
    const [form] = Form.useForm();
    const { dark } = useTheme();
    const [loading, setLoading] = React.useState(false);
    const [userType, setUserType] = useState(initialData?.userType || UserType.STAFF);
    const [selectedBranch, setSelectedBranch] = useState<string>('');
    const [resetPassword, setResetPassword] = useState(false);

    // Queries
    const { data: branchesResponse } = useGetAllBranches({
        query: { enabled: open }
    });

    // Password validation rules based on config
    const getPasswordRules = () => {
        const rules = [
            { required: true, message: 'Password is required' },
            { min: passwordConfig.minimumLength, message: `Password must be at least ${passwordConfig.minimumLength} characters` },
        ];

        if (passwordConfig.requireUppercase) {
            rules.push({
                // @ts-ignore
                pattern: /[A-Z]/,
                message: 'Password must include at least one uppercase letter'
            });
        }

        if (passwordConfig.requireLowercase) {
            rules.push({
                // @ts-ignore
                pattern: /[a-z]/,
                message: 'Password must include at least one lowercase letter'
            });
        }

        if (passwordConfig.requireNumbers) {
            rules.push({
                // @ts-ignore
                pattern: /\d/,
                message: 'Password must include at least one number'
            });
        }

        if (passwordConfig.requireSpecialChars) {
            rules.push({
                // @ts-ignore
                pattern: /[!@#$%^&*(),.?":{}|<>]/,
                message: 'Password must include at least one special character'
            });
        }

        return rules;
    };

    useEffect(() => {
        if (open && initialData) {
            setUserType(initialData.userType || UserType.STAFF);
            setSelectedBranch(initialData.branch || '');
            form.setFieldsValue({
                ...initialData,
                groups: initialData.groups?.map(g => g.code),
                primaryGroup: initialData.primaryGroup?.code,
            });
        } else {
            form.resetFields();
            setUserType(UserType.STAFF);
            setSelectedBranch('');
            setResetPassword(false);
        }
    }, [open, initialData, form]);

    const [searchQuery, setSearchQuery] = useState('');

    const { data: customersResponse, isFetching: isSearching } = useSearchCustomers1(
        selectedBranch,
        { query: searchQuery },
        {
            query: {
                enabled: !!selectedBranch && searchQuery.length >= 2,
            }
        }
    );

    const handleCustomerSearch = (value: string) => {
        setSearchQuery(value);
    };

    const handleBranchChange = (value: string) => {
        setSelectedBranch(value);
        form.setFieldsValue({ custNo: undefined });
    };

    const handleCustomerSelect = (custNo: string) => {
        const customer = customersResponse?.find((c: any) => c.custNo === custNo);
        if (customer) {
            const currentUserId = form.getFieldValue('userId');
            const userIdTouched = form.isFieldTouched('userId');
            const nameParts = (customer.name || '').trim().split(/\s+/).filter(Boolean);
            const firstName = nameParts[0];
            const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : undefined;
            const suggestedUserId = firstName
                ? `${firstName.toLowerCase()}${lastName ? `.${lastName.toLowerCase()}` : ''}`
                : undefined;

            form.setFieldsValue({
                fullName: customer.name,
                phoneNo: customer.phone,
                custNo: customer.custNo,
                userId: (!userIdTouched && (!currentUserId || String(currentUserId).trim() === '') && suggestedUserId)
                    ? suggestedUserId
                    : currentUserId,
            });
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();

            if (mode === 'delete') {
                await onSubmit(initialData as NubUserDTO);
            } else {
                const submitData: NubUserDTO = {
                    ...values,
                    userType,
                    fsUserId: userType === UserType.STAFF ? values.fsUserId : undefined,
                    resetPassword,
                    groups: groups.filter(g => values.groups.includes(g.code)),
                    primaryGroup: groups.find(g => g.code === values.primaryGroup),
                };

                // Only include password if:
                // 1. Adding new user and admin sets password
                // 2. Editing user, resetting password, and admin sets password
                if ((mode === 'add' && passwordConfig.initialPassword) ||
                    (mode === 'edit' && resetPassword && passwordConfig.initialPassword)) {
                    submitData.password = values.password;
                }

                await onSubmit(submitData);
            }
        } catch (error) {
            console.error('Form validation failed:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={
                mode === 'view' ? 'View User Details' :
                    mode === 'add' ? 'Create New User' :
                        mode === 'edit' ? 'Edit User' :
                            'Delete User'
            }
            open={open}
            onCancel={onCancel}
            onOk={handleSubmit}
            confirmLoading={loading}
            width={800}
            className={dark ? 'dark' : ''}
        >
            {mode === 'delete' ? (
                <Alert
                    message="Delete User"
                    description={`Are you sure you want to delete ${initialData?.fullName}?`}
                    type="error"
                    showIcon
                />
            ) : (
                <Form
                    form={form}
                    layout="vertical"
                    disabled={mode === 'view'}
                >
                    <Divider>User Type & Branch</Divider>

                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item label="User Type" required>
                            <Radio.Group
                                value={userType}
                                onChange={e => setUserType(e.target.value)}
                            >
                                <Radio.Button value={UserType.STAFF}>Staff</Radio.Button>
                                <Radio.Button value={UserType.MEMBER}>Member</Radio.Button>
                                <Radio.Button value={UserType.SYSTEM}>System</Radio.Button>
                            </Radio.Group>
                        </Form.Item>

                        <Form.Item
                            name="branch"
                            label="Branch"
                            rules={[{ required: true, message: 'Please select branch' }]}
                        >
                            <Select
                                showSearch
                                placeholder="Select branch"
                                onChange={handleBranchChange}
                            >
                                {branchesResponse?.map((branch: any) => (
                                    <Option key={branch.branch} value={branch.branch}>
                                        {branch.branchName}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>

                    {userType === UserType.MEMBER && (
                        <Form.Item
                            name="custNo"
                            label="Select Customer"
                            rules={[{ required: true, message: 'Please select customer' }]}
                        >
                            <Select
                                showSearch
                                placeholder="Search customer by name"
                                onSearch={handleCustomerSearch}
                                onChange={handleCustomerSelect}
                                filterOption={false}
                                loading={isSearching}
                                notFoundContent={isSearching ? <Spin size="small" /> : "No customers found"}
                            >
                                {customersResponse?.map((customer: any) => (
                                    <Option key={customer.custNo} value={customer.custNo}>
                                        {customer.name} ({customer.custNo})
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}

                    <Divider>Basic Information</Divider>

                    <Form.Item
                        name="userId"
                        label="Username"
                        rules={[{ required: true, message: 'Username is required' }]}
                    >
                        <Input prefix={<UserOutlined />} disabled={mode === 'edit'} />
                    </Form.Item>

                    <Form.Item
                        name="fullName"
                        label="Full Name"
                        rules={[{ required: true, message: 'Full name is required' }]}
                    >
                        <Input
                            prefix={<UserOutlined />}
                            disabled={userType === UserType.MEMBER}
                        />
                    </Form.Item>

                    {userType === UserType.STAFF && (
                        <Form.Item
                            name="fsUserId"
                            label="FS User ID"
                            rules={[{ max: 30, message: 'FS User ID must be at most 30 characters' }]}
                        >
                            <Input maxLength={30} placeholder="Enter FS User ID" />
                        </Form.Item>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[
                                { required: true, message: 'Email is required' },
                                { type: 'email', message: 'Please enter a valid email' }
                            ]}
                        >
                            <Input prefix={<MailOutlined />} />
                        </Form.Item>

                        <Form.Item
                            name="phoneNo"
                            label="Phone Number"
                            rules={[{ required: true, message: 'Phone number is required' }]}
                        >
                            <Input prefix={<PhoneOutlined />} />
                        </Form.Item>
                    </div>

                    {mode === 'edit' && (
                        <Form.Item name="resetPassword" valuePropName="checked">
                            <Checkbox onChange={(e) => setResetPassword(e.target.checked)}>
                                Reset Password
                            </Checkbox>
                        </Form.Item>
                    )}

                    {((mode === 'add' && passwordConfig.initialPassword) ||
                        (mode === 'edit' && resetPassword && passwordConfig.initialPassword)) && (
                        <Form.Item
                            name="password"
                            label="Password"
                            rules={getPasswordRules()}
                            help={
                                <div className="text-xs text-gray-500">
                                    Password must:
                                    <ul className="list-disc pl-4">
                                        <li>Be at least {passwordConfig.minimumLength} characters long</li>
                                        {passwordConfig.requireUppercase && <li>Include uppercase letters</li>}
                                        {passwordConfig.requireLowercase && <li>Include lowercase letters</li>}
                                        {passwordConfig.requireNumbers && <li>Include numbers</li>}
                                        {passwordConfig.requireSpecialChars && <li>Include special characters</li>}
                                    </ul>
                                </div>
                            }
                        >
                            <Input.Password prefix={<LockOutlined />} />
                        </Form.Item>
                    )}

                    <Divider>Access Control</Divider>

                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item
                            name="primaryGroup"
                            label="Primary Group"
                            rules={[{ required: true, message: 'Primary group is required' }]}
                        >
                            <Select
                                placeholder="Select primary group"
                                onChange={(value) => {
                                    const currentGroups = form.getFieldValue('groups') || [];
                                    if (!currentGroups.includes(value)) {
                                        form.setFieldsValue({
                                            groups: [...currentGroups, value]
                                        });
                                    }
                                }}
                            >
                                {groups.filter(g => g.primary).map(group => (
                                    <Option key={group.code} value={group.code}>
                                        {group.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="groups"
                            label="Additional Groups"
                            rules={[
                                { required: true, message: 'At least one group is required' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        const primaryGroup = getFieldValue('primaryGroup');
                                        if (!value || value.includes(primaryGroup)) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Groups must include the primary group'));
                                    },
                                }),
                            ]}
                        >
                            <Select
                                mode="multiple"
                                placeholder="Select groups"
                                onChange={(values) => {
                                    const primaryGroup = form.getFieldValue('primaryGroup');
                                    if (primaryGroup && !values.includes(primaryGroup)) {
                                        form.setFieldsValue({
                                            groups: [...values, primaryGroup]
                                        });
                                    }
                                }}
                            >
                                {groups.map(group => (
                                    <Option
                                        key={group.code}
                                        value={group.code}
                                        disabled={form.getFieldValue('primaryGroup') === group.code}
                                    >
                                        {group.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="enabled"
                        label="Account Status"
                        valuePropName="checked"
                    >
                        <Switch
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                            defaultChecked
                        />
                    </Form.Item>
                </Form>
            )}
        </Modal>
    );
};